import { useEffect, useState } from "react";

import { FileText } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DOCUMENT_CLASSIFICATIONS } from "@/constants/documents";

import { DocSorterFileSchema } from "@api/generated/types.gen";

type ManualClassificationModalProps = {
  file: DocSorterFileSchema | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (fileId: string, classification: string) => Promise<void>;
};

export const ManualClassificationModal = ({
  file,
  isOpen,
  onClose,
  onSave,
}: ManualClassificationModalProps) => {
  const [selectedClassification, setSelectedClassification] = useState<string>("");

  // Initialize form when file changes
  useEffect(() => {
    if (file) {
      setSelectedClassification(file.classification || "");
    }
  }, [file]);

  const handleSave = async () => {
    if (!file || !selectedClassification) return;

    await onSave(file.id, selectedClassification);
  };

  // Check if the classification has changed
  const currentClassification = file?.classification || "";
  const hasChanged = selectedClassification !== currentClassification;

  const handleCancel = () => {
    // Reset form to original values
    if (file) {
      setSelectedClassification(file.classification || "");
    }
    onClose();
  };

  if (!file) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Manual Classification
          </DialogTitle>
          <DialogDescription>
            Select a classification for this document.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="classification" className="text-right">
              Classification
            </Label>
            <div className="col-span-3">
              <Select
                value={selectedClassification}
                onValueChange={setSelectedClassification}
              >
                <SelectTrigger id="classification" className="w-full">
                  <SelectValue placeholder="Select a classification" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CLASSIFICATIONS.map((docType) => (
                    <SelectItem key={docType.id} value={docType.category}>
                      {docType.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="simple-outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!selectedClassification || !hasChanged}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
